import React from "react";
import { colors } from "../styles/designSystem";
import { PipelineFlow, StepGrid, CycleFlow, DomainGrid } from "./diagrams/primitives";

/**
 * Diagrams for the Verizon case study.
 *
 * Like the Caterpillar set, every export here is a composition of the shared
 * primitives — no styled-components in this file. Which is the same argument the
 * page itself makes: define the pattern once, and pages become data.
 */

const SYSTEM_LADDER = [
    { name: "Tokens", desc: "Colour, type, spacing, and state as values rather than decisions repeated per screen", color: colors.cyan[300] },
    { name: "Components", desc: "Behaviour, variants, and states — the smallest reusable unit a product team consumes", color: colors.primary[500] },
    { name: "Patterns", desc: "Compositions that solve a recurring interaction the same way everywhere", color: colors.emerald[500] },
    { name: "Workflows", desc: "Multi-step product journeys assembled from those patterns", color: colors.amber[400] },
    { name: "Products", desc: "Account, billing, commerce, and support, all built from the same layers", color: colors.coral[400] },
];

export const SystemLadder = () => (
    <PipelineFlow
        label="The System as Infrastructure"
        stages={SYSTEM_LADDER}
        caption="Once each layer is a contract the layer above depends on, teams stop re-solving the same interaction and start assembling from what already exists."
    />
);

export const SystemLoop = () => (
    <CycleFlow
        label="System and Product on One Loop"
        nodes={[
            { name: "Design System", color: colors.primary[500] },
            { name: "Product Implementation", color: colors.cyan[300] },
            { name: "Real Requirements", color: colors.amber[400] },
            { name: "Edge Cases", color: colors.coral[400] },
            { name: "System Improvements", color: colors.emerald[500] },
        ]}
        note="Improvements flow back out to every product using the pattern"
        noteColor={colors.emerald[500]}
        caption="A component that behaves perfectly in documentation can still fall apart inside a purchase flow. Building on both sides of the boundary meant the product was what tested the system."
    />
);

const COMMERCE_STATE = [
    { name: "Account", color: colors.cyan[300] },
    { name: "Existing Lines", color: colors.cyan[300] },
    { name: "Device", color: colors.primary[500] },
    { name: "Eligibility", color: colors.primary[500] },
    { name: "Promotion", color: colors.amber[400] },
    { name: "Plan", color: colors.amber[400] },
    { name: "Accessories", color: colors.emerald[500] },
    { name: "Billing", color: colors.emerald[500] },
];

export const CommerceState = () => (
    <StepGrid
        label="What Determines a Telecom Purchase"
        steps={COMMERCE_STATE}
        columns={4}
        caption="Every one of these can change what a customer is allowed to buy. Telecom commerce carries far more state than a conventional checkout, and the interface has to express that state without exposing the systems underneath it."
    />
);

const PROMOTION_FLOW = [
    { name: "Customer Context", desc: "Account, tenure, existing lines, current devices", color: colors.cyan[300] },
    { name: "Eligibility", desc: "What this customer actually qualifies for right now", color: colors.primary[500] },
    { name: "Offer", desc: "The promotion expressed in terms of their account, not marketing copy", color: colors.amber[400] },
    { name: "Configuration", desc: "Device, plan, and accessory decisions the offer depends on", color: colors.emerald[500] },
    { name: "Purchase", desc: "What changes, what it costs, and what happens next", color: colors.coral[400] },
];

export const PromotionFlow = () => (
    <PipelineFlow
        label="Promotions as Product Workflow"
        stages={PROMOTION_FLOW}
        caption="Built into the commerce experience rather than layered on top of it, so promotional logic became part of the workflow instead of a separate marketing screen."
    />
);

const NAV_DOMAINS = [
    { name: "Account", desc: "Profile, plan, and the settings a customer expects to own.", color: colors.cyan[300] },
    { name: "Lines", desc: "Managing each line on the account independently of the account itself.", color: colors.primary[500] },
    { name: "Devices", desc: "What the customer has, and what they can do with it.", color: colors.emerald[500] },
    { name: "Billing", desc: "Charges, payments, and the explanation of both.", color: colors.amber[400] },
    { name: "Shop", desc: "Upgrades, new lines, and accessories, entered from anywhere.", color: colors.coral[400] },
    { name: "Support", desc: "Help that stays discoverable as the product keeps growing.", color: colors.purple[400] },
];

export const NavigationDomains = () => (
    <DomainGrid label="Navigation Is Information Architecture" domains={NAV_DOMAINS} />
);

export const QALoop = () => (
    <CycleFlow
        label="Ownership Past Handoff"
        nodes={[
            { name: "Design", color: colors.primary[500] },
            { name: "Implementation", color: colors.cyan[300] },
            { name: "Product Behaviour", color: colors.emerald[500] },
            { name: "Edge Cases", color: colors.coral[400] },
            { name: "Refinement", color: colors.amber[400] },
            { name: "Release", color: colors.purple[400] },
        ]}
        note="Implementation surfaced conditions the specification never named"
        noteColor={colors.coral[400]}
    />
);

const OWNED_DOMAINS = [
    { name: "Design Systems Engineering", desc: "Reusable components and patterns built to work across products rather than individual screens.", color: colors.primary[500] },
    { name: "Component Architecture", desc: "Component behaviour, variants, states, and composition across complex product scenarios.", color: colors.cyan[300] },
    { name: "Product Implementation", desc: "Working across the system and the customer-facing application, carrying system decisions into production.", color: colors.emerald[500] },
    { name: "Accessibility", desc: "Accessible behaviour established at the reusable layer instead of re-solved per feature.", color: colors.amber[400] },
    { name: "Commerce Workflows", desc: "Account, eligibility, promotion, device, and purchasing requirements turned into understandable flows.", color: colors.coral[400] },
    { name: "Production QA", desc: "Implementation, testing, edge cases, and refinement — ownership that did not end at handoff.", color: colors.purple[400] },
];

export const OwnedDomains = () => (
    <DomainGrid label="What I Owned" domains={OWNED_DOMAINS} />
);
